import {useEffect} from 'react'
import {useLocation} from 'react-router'
import {track} from '@core/utils/analytics'
import {GATE_TRIGGER_ONE_TAP} from '@core/auth/signInMethods'
import './GoogleOneTap.scss'

type CredentialResponse = {credential?: string}
type GoogleId = {
    initialize(options: Record<string, unknown>): void
    prompt(): void
    cancel(): void
}
declare global {
    interface Window {
        google?: {accounts: {id: GoogleId}}
    }
}

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

function loadScript(): Promise<void> {
    if (window.google?.accounts?.id) return Promise.resolve()
    return new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
        const script = existing ?? Object.assign(document.createElement('script'), {src: SCRIPT_SRC, async: true})
        script.addEventListener('load', () => resolve())
        script.addEventListener('error', () => reject(new Error('Google Identity Services failed to load')))
        if (!existing) document.head.appendChild(script)
    })
}

/** Posts the ID token to the One Tap route as a full-document form, so the signed-in page loads
 *  whole, as it does after the other sign-ins. */
function submitCredential(credential: string, redirectTo: string) {
    const form = Object.assign(document.createElement('form'), {method: 'POST', action: '/auth/google/one-tap'})
    for (const [name, value] of [['credential', credential], ['redirectTo', redirectTo]]) {
        form.appendChild(Object.assign(document.createElement('input'), {type: 'hidden', name, value}))
    }
    document.body.appendChild(form)
    form.submit()
}

/**
 * Google One Tap: Google's own prompt, offered to a signed-out visitor whose browser is signed in
 * to Google. Mounted once by the root for products that turn it on; it prompts on the first page
 * and Google itself holds the visitor's dismissals (its cool-down grows with each one). The sign-in
 * pages are left to their own buttons.
 */
export function GoogleOneTap({clientId}: {clientId: string}) {
    const {pathname} = useLocation()
    const onAuthPage = pathname.startsWith('/auth/')

    useEffect(() => {
        if (onAuthPage) return
        let cancelled = false
        loadScript()
            .then(() => {
                if (cancelled || !window.google) return
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    // The page at the moment of the tap, which client navigation may have moved on.
                    callback: ({credential}: CredentialResponse) => {
                        if (!credential) return
                        track('auth_option_selected', {method: 'google', trigger: GATE_TRIGGER_ONE_TAP})
                        submitCredential(credential, window.location.pathname + window.location.search)
                    },
                    use_fedcm_for_prompt: true,
                    cancel_on_tap_outside: true,
                    context: 'signin',
                })
                window.google.accounts.id.prompt()
            })
            .catch(() => {})
        return () => {
            cancelled = true
            window.google?.accounts.id.cancel()
        }
    }, [clientId, onAuthPage])

    return null
}
